import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Users, HardDrive, Zap, AlertTriangle, TrendingUp, 
  Database, FileText, Briefcase, UserCheck, Calendar,
  ArrowRight, Crown, BarChart3, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/contexts/I18nContext';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#14b8a6', '#f97316'];

export default function TenantUsageDashboard() {
  const { t } = useI18n();
  const [usage, setUsage] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsageData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [usageRes, recsRes] = await Promise.all([
        axios.get(`${API}/usage/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/usage/recommendations`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setUsage(usageRes.data);
      setRecommendations(recsRes.data.recommendations || []);
    } catch (error) {
      console.error('Failed to fetch usage data:', error);
      toast.error('Failed to fetch usage data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!usage) return null;

  const dataBreakdownArray = Object.entries(usage.data_breakdown || {})
    .filter(([_, count]) => count > 0)
    .map(([name, value], index) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' '),
      value,
      color: COLORS[index % COLORS.length]
    }));

  return (
    <div className="space-y-6" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('usage.title', 'Usage Dashboard')}</h1>
          <p className="text-slate-500 mt-1">Monitor your resource consumption and limits</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="bg-violet-100 text-violet-700 text-sm px-3 py-1">
            <Crown className="h-4 w-4 mr-1" />
            {usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1)} Plan
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.location.href = '/subscription'}
          >
            Upgrade Plan
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {usage.alerts && usage.alerts.length > 0 && (
        <div className="space-y-3">
          {usage.alerts.map((alert, idx) => (
            <Alert 
              key={`tena-${idx}`}
              className={`${
                alert.type === 'critical' 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-amber-50 border-amber-200'
              }`}
            >
              <AlertTriangle className={`h-4 w-4 ${
                alert.type === 'critical' ? 'text-red-600' : 'text-amber-600'
              }`} />
              <AlertDescription className={
                alert.type === 'critical' ? 'text-red-700' : 'text-amber-700'
              }>
                <strong>{alert.message}</strong> - {alert.action}
              </AlertDescription>
            </Alert>
          ))}
        </div>
      )}

      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Users */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between mb-3">
              <span className="text-3xl font-bold text-slate-900">
                {usage.usage.users.current}
              </span>
              <span className="text-slate-500">
                / {usage.usage.users.limit}
              </span>
            </div>
            <Progress 
              value={usage.percentages.users} 
              className={`h-2 ${usage.percentages.users >= 90 ? '[&>div]:bg-red-500' : usage.percentages.users >= 75 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`}
            />
            <p className="text-xs text-slate-500 mt-2">
              {usage.usage.users.remaining === 'Unlimited' 
                ? 'Unlimited users' 
                : `${usage.usage.users.remaining} remaining`}
            </p>
          </CardContent>
        </Card>

        {/* Storage */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <HardDrive className="h-4 w-4" />
              Storage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between mb-3">
              <span className="text-3xl font-bold text-slate-900">
                {usage.usage.storage.current_mb < 1024 
                  ? `${usage.usage.storage.current_mb} MB`
                  : `${usage.usage.storage.current_gb} GB`}
              </span>
              <span className="text-slate-500">
                / {usage.usage.storage.limit_gb === 'Unlimited' ? '∞' : `${usage.usage.storage.limit_gb} GB`}
              </span>
            </div>
            <Progress 
              value={usage.percentages.storage} 
              className={`h-2 ${usage.percentages.storage >= 90 ? '[&>div]:bg-red-500' : usage.percentages.storage >= 75 ? '[&>div]:bg-amber-500' : '[&>div]:bg-blue-500'}`}
            />
            <p className="text-xs text-slate-500 mt-2">
              {usage.usage.storage.remaining_gb === 'Unlimited'
                ? 'Unlimited storage'
                : `${usage.usage.storage.remaining_gb} GB remaining`}
            </p>
          </CardContent>
        </Card>

        {/* API Usage */}
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
              <Zap className="h-4 w-4" />
              API Requests (This Minute)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between mb-3">
              <span className="text-3xl font-bold text-slate-900">
                {usage.usage.api.requests_this_minute}
              </span>
              <span className="text-slate-500">
                / {usage.usage.api.limit_per_minute}
              </span>
            </div>
            <Progress 
              value={usage.percentages.api_minute} 
              className={`h-2 ${usage.percentages.api_minute >= 80 ? '[&>div]:bg-amber-500' : '[&>div]:bg-violet-500'}`}
            />
            <div className="flex justify-between text-xs text-slate-500 mt-2">
              <span>Hour: {usage.usage.api.requests_this_hour} / {usage.usage.api.limit_per_hour}</span>
              <span>Day: {usage.usage.api.requests_today}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Data Breakdown & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Breakdown */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-violet-600" />
              Data Breakdown
            </CardTitle>
            <CardDescription>Records by category</CardDescription>
          </CardHeader>
          <CardContent>
            {dataBreakdownArray.length > 0 ? (
              <div className="flex items-center gap-6">
                <div className="w-40 h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={dataBreakdownArray}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="value"
                      >
                        {dataBreakdownArray.map((entry, index) => (
                          <Cell key={`tena-${entry.name || index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {dataBreakdownArray.slice(0, 6).map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-slate-600">{item.name}</span>
                      </div>
                      <span className="font-medium text-slate-900">{item.value.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Database className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No data yet. Start by creating positions and adding candidates.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity Overview */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Activity Overview
            </CardTitle>
            <CardDescription>User activity and engagement</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <UserCheck className="h-5 w-5 text-emerald-600" />
                  <span className="text-slate-700">Active Users (30 days)</span>
                </div>
                <span className="text-xl font-bold text-slate-900">
                  {usage.usage.activity.active_users_30d}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span className="text-slate-700">Total Users</span>
                </div>
                <span className="text-xl font-bold text-slate-900">
                  {usage.usage.activity.total_users}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-violet-600" />
                  <span className="text-slate-700">Subscription Expires</span>
                </div>
                <span className="text-sm font-medium text-slate-900">
                  {usage.subscription_expires 
                    ? new Date(usage.subscription_expires).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recommendations.map((rec, idx) => (
                <div 
                  key={`tena-${idx}`}
                  className={`p-4 rounded-lg border ${
                    rec.priority === 'high' 
                      ? 'bg-red-50 border-red-200' 
                      : rec.priority === 'medium'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-blue-50 border-blue-200'
                  }`}
                >
                  <h4 className={`font-medium ${
                    rec.priority === 'high' 
                      ? 'text-red-900' 
                      : rec.priority === 'medium'
                      ? 'text-amber-900'
                      : 'text-blue-900'
                  }`}>
                    {rec.title}
                  </h4>
                  <p className={`text-sm mt-1 ${
                    rec.priority === 'high' 
                      ? 'text-red-700' 
                      : rec.priority === 'medium'
                      ? 'text-amber-700'
                      : 'text-blue-700'
                  }`}>
                    {rec.description}
                  </p>
                  {rec.action && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 p-0 h-auto"
                      onClick={() => window.location.href = rec.action}
                    >
                      Take action <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Limits Reference */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Your Plan Limits</CardTitle>
          <CardDescription>Current limits based on your {usage.plan} plan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{usage.limits.users}</p>
              <p className="text-xs text-slate-500">Users</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{usage.limits.storage_gb} GB</p>
              <p className="text-xs text-slate-500">Storage</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{usage.limits.api_per_minute}</p>
              <p className="text-xs text-slate-500">API/min</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{usage.limits.api_per_hour}</p>
              <p className="text-xs text-slate-500">API/hour</p>
            </div>
            <div className="text-center p-3 bg-slate-50 rounded-lg">
              <p className="text-2xl font-bold text-slate-900">{usage.limits.api_per_day}</p>
              <p className="text-xs text-slate-500">API/day</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
